import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Logo from "./Logo";

const effectiveDate = "June 2026";

const sections = [
  {
    heading: "1. Introduction",
    body: [
      "Brave Me (\u201cwe,\u201d \u201cus,\u201d or \u201cour\u201d) is committed to protecting the privacy of the families, children, and visitors who use our website. This Privacy Policy explains what information we collect, how we use it, and the choices you have. By using our website, you agree to the practices described below.",
    ],
  },
  {
    heading: "2. Information We Collect",
    body: [
      "We only collect information you choose to give us. This may include:",
    ],
    list: [
      "Newsletter sign-ups: the email address you submit so we can share Brave Me news and updates.",
      "Brave Me Kid of the Week nominations: the brave kid\u2019s first name, age, and region, their story, and the name, email, and phone number of the submitting adult.",
      "Basic technical data: standard information your browser shares automatically, such as device type and general usage, used only to keep the site working well.",
    ],
  },
  {
    heading: "3. How We Use Your Information",
    body: ["We use the information you provide to:"],
    list: [
      "Send you our newsletter and Brave Me updates when you subscribe.",
      "Review and, with permission, feature Brave Me Kid of the Week nominations.",
      "Respond to your questions and requests.",
      "Operate, maintain, and improve our website.",
    ],
  },
  {
    heading: "4. Children\u2019s Privacy",
    body: [
      "Brave Me is designed to empower children, but our website is intended to be used together with a parent or guardian. We do not knowingly collect personal information directly from children under 13 without verifiable parental consent. Kid of the Week nominations must be submitted by a parent, guardian, or responsible adult, who provides their own contact details and consents on the child\u2019s behalf.",
      "If you believe a child has provided us with personal information without parental consent, please contact us at bebrave@braveme.com and we will promptly delete it.",
    ],
  },
  {
    heading: "5. How We Share Information",
    body: [
      "We do not sell or rent your personal information. We may share information only with trusted service providers who help us operate the site (for example, video hosting and e-commerce partners), and only as needed to provide those services. We may also disclose information if required by law.",
    ],
  },
  {
    heading: "6. Third-Party Services",
    body: [
      "Our website uses third-party tools to deliver content and features, including Vimeo for educational videos and Shopify for the Courage Canyon Outfitters shop. These services may collect information under their own privacy policies. We encourage you to review their policies when you interact with them.",
    ],
  },
  {
    heading: "7. Data Retention & Security",
    body: [
      "We keep the information you provide only as long as needed for the purposes described in this policy, and we take reasonable measures to protect it. No method of transmission or storage is completely secure, but we work to safeguard your information.",
    ],
  },
  {
    heading: "8. Your Choices",
    body: [
      "You can unsubscribe from our newsletter at any time using the link in our emails. To update or delete information you\u2019ve submitted, including a Kid of the Week nomination, just contact us and we\u2019ll take care of it.",
    ],
  },
  {
    heading: "9. Changes to This Policy",
    body: [
      "We may update this Privacy Policy from time to time. When we do, we\u2019ll revise the date below. Your continued use of the website means you accept the updated policy.",
    ],
  },
  {
    heading: "10. Contact Us",
    body: [
      "If you have any questions about this Privacy Policy or your information, please reach out:",
    ],
    list: ["Email: bebrave@braveme.com", "Phone: (855) 427-2838"],
  },
];

const PrivacyPolicy = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white" data-testid="privacy-policy-page">
      <header className="border-b border-gray-100 bg-white">
        <div className="max-w-4xl mx-auto px-5 md:px-8 h-[72px] md:h-[88px] flex items-center justify-between">
          <Link to="/" aria-label="Brave Me home" data-testid="privacy-logo-home">
            <Logo imgClassName="h-[48px] md:h-[60px]" />
          </Link>
          <Link
            to="/"
            className="inline-flex items-center gap-2 font-display text-sm md:text-[15px] text-[#187EE4] hover:text-[#FF6C10] transition-colors"
            data-testid="privacy-back-home"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-5 md:px-8 py-12 md:py-16">
        <h1 className="font-display text-4xl sm:text-5xl mb-3" style={{ color: "#187EE4" }}>
          Privacy Policy
        </h1>
        <p className="text-slate-500 font-medium mb-10">Effective date: {effectiveDate}</p>

        <div className="space-y-9">
          {sections.map((s) => (
            <section key={s.heading} data-testid="privacy-section">
              <h2 className="font-display text-xl md:text-2xl mb-3" style={{ color: "#FF6C10" }}>
                {s.heading}
              </h2>
              {s.body.map((p, i) => (
                <p key={i} className="text-slate-600 text-base leading-relaxed mb-3">
                  {p}
                </p>
              ))}
              {s.list && (
                <ul className="space-y-2 pl-1">
                  {s.list.map((item) => (
                    <li key={item} className="flex gap-3 text-slate-600 text-base leading-relaxed">
                      <span
                        className="mt-2 shrink-0 w-1.5 h-1.5 rounded-full"
                        style={{ backgroundColor: "#F1C91F" }}
                      />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          ))}
        </div>
      </main>

      <footer className="text-white" style={{ backgroundColor: "#0f2540" }}>
        <div className="max-w-3xl mx-auto px-5 md:px-8 py-8 flex flex-col md:flex-row items-center justify-between gap-3">
          <p className="text-white/60 text-sm">
            &copy; {new Date().getFullYear()} Brave Me. All rights reserved.
          </p>
          <Link to="/" className="text-white/70 hover:text-white text-sm font-semibold transition-colors">
            Return to Brave Me
          </Link>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicy;
